import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { getRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import type { QueryConfig } from 'src/lib/react-query'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import type { InviteFilters, InviteResponse } from 'src/shared/schemas/invites/invite'

interface ListInvitesParams {
  filters?: InviteFilters
  skip?: number
  limit?: number
}

// TODO: Change limit to 10 once we implement pagination
export const listInvites = async ({ filters, skip = 0, limit = 1000 }: ListInvitesParams): Promise<InviteResponse> => {
  const response = await getRequest<InviteResponse>('/users/invites/me', {
    params: {
      ...filters,
      skip,
      limit,
    },
  })
  if (!response) throw new Error('Failed to fetch invites')
  return response
}

export const useListInvites = (params: ListInvitesParams, config?: QueryConfig<typeof listInvites>) => {
  return useQuery({
    ...queries.invites.list,
    queryFn: () => listInvites(params),
    ...config,
  })
}

export const useUserInvites = ({
  completed,
  limit = 10,
  skip = 0,
}: {
  completed?: boolean
  limit?: number
  skip?: number
}) => {
  const { currentAuth } = useAuthState()

  return useInfiniteQuery({
    ...queries.invites.infiniteList({ completed, limit, skip }),
    queryFn: async ({ pageParam = 0 }) => {
      try {
        if (!currentAuth?.email) return null

        const response = await getRequest<InviteResponse>('/users/invites/me', {
          params: {
            completed,
            skip: pageParam * limit,
            limit,
          },
        })

        return response
      } catch (error) {
        console.error('Error fetching user invites:', error)
        // return null
        throw error
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.items?.length === 0) return undefined
      return allPages.length
    },
    initialPageParam: 0,
  })
}
